import React, { useEffect, useMemo, useState } from "react";
import styles from "./languageSelectorModalMobile.module.scss";
import Button from "@Components/Button";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import wrapperHOC from "@Utils/wrapperHOC";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { languagesForAmberUserWebsite } from "locales/langConfig/langConfig";
import useLanguageSelector from "hooks/useLanguageSelector";
import config from "@Config/index";
import currencyData, { ALLOWED_CURRENCY_CODES } from "constants/currencyConstants";
import setCookie from "helpers/setCookie";
import getCookie from "helpers/getCookie";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import useIsApp from "hooks/useIsApp";

type LanguageOption = {
  code: string;
  label: string;
  flag?: any;
};

interface LanguageSelectorModalMobileProps {
  onClose: () => void;
  isCurrencyFeatureEnabled: boolean;
  initialTab?: "language" | "currency";
}

const getAvailableLanguages = (
  userLanguage: string,
  excludeCodes: string[] = [],
): LanguageOption[] => {
  const languages = languagesForAmberUserWebsite
    .filter((lang) => !excludeCodes.includes(lang.code))
    .map((lang) => ({
      code: lang.code,
      label: lang.nativeLabel,
      flag: lang.flag,
    }));

  return languages.sort((a, b) => {
    if (a.code === userLanguage) return -1;
    if (b.code === userLanguage) return 1;
    return 0;
  });
};

const LanguageSelectorModalMobile: React.FC<LanguageSelectorModalMobileProps> = ({
  onClose,
  isCurrencyFeatureEnabled,
  initialTab = "language",
}) => {
  const { t } = useTranslationHook();
  const isApp = useIsApp();

  const { userLanguage } = useSelector(({ appData }: AppState) => ({
    userLanguage: appData.userLanguage,
  }));

  const handleEventEmitter = ({ action, ...rest }: any) => {
    gtagEventEmitter({ action, category: "LANGUAGE_MODAL", ...rest });
  };

  const { handleLanguageChange } = useLanguageSelector({ handleEventEmitter });

  const defaultLanguage = useMemo(() => userLanguage || "en", [userLanguage]);

  const [activeTab, setActiveTab] = useState<"language" | "currency">(
    initialTab === "currency" && isCurrencyFeatureEnabled ? "currency" : "language",
  );
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [selectedCurrency, setSelectedCurrency] = useState("default");
  const [currencies, setCurrencies] = useState<typeof currencyData>([]);

  /** Sync language with redux */
  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  /** Setup currency list + cookie ordering */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled) return;
    const userCurrencyCookie = getCookie("user_currency");
    const filteredCurrencies = currencyData.filter((cur) =>
      ALLOWED_CURRENCY_CODES.includes(cur.standard_name),
    );
    const orderedCurrencies = [...filteredCurrencies];
    if (userCurrencyCookie) {
      setSelectedCurrency(userCurrencyCookie);
      orderedCurrencies.sort((a, b) => {
        if (a.standard_name === userCurrencyCookie) return -1;
        if (b.standard_name === userCurrencyCookie) return 1;
        return 0;
      });
    }
    setCurrencies(orderedCurrencies);
  }, [isCurrencyFeatureEnabled]);

  /** Safety: reset tab if currency feature is disabled */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled && activeTab === "currency") {
      setActiveTab("language");
    }
  }, [isCurrencyFeatureEnabled, activeTab]);

  const onApply = () => {
    // Close modal first to ensure clean state before navigation
    onClose();

    if (activeTab === "language") {
      handleEventEmitter({ action: "APPLY_LANGUAGE", language: selectedLanguage });
      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        handleLanguageChange(selectedLanguage);
      }, 100);
    } else {
      handleEventEmitter({ action: "APPLY_CURRENCY", currency: selectedCurrency });

      // Notify native app (only in app) so it can persist currency to MMKV
      if (isApp) {
        sendWebViewEvent(WEB_VIEW_APP_EVENTS.CURRENCY_CHANGED, {
          currency: selectedCurrency === "default" ? "" : selectedCurrency,
        });
      }

      const baseDomain = window.location.hostname.split(".").slice(-2).join(".");
      if (selectedCurrency === "default") {
        // delete cookie if exists
        setCookie({
          cname: "user_currency",
          cvalue: "",
          exdays: -1, // expire immediately
          secure: true,
          domain: `.${baseDomain}`,
        });
      } else {
        // set cookie
        setCookie({
          cname: "user_currency",
          cvalue: selectedCurrency,
          exdays: 365,
          secure: true,
          domain: `.${baseDomain}`,
        });
      }
      // Use setTimeout to ensure modal closes before reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const onReset = () => {
    setSelectedLanguage(defaultLanguage);
    if (isCurrencyFeatureEnabled) setSelectedCurrency("default");
  };

  const handleActiveTabChange = (tab: "language" | "currency") => {
    setActiveTab(tab);
    handleEventEmitter({ action: `clicked_${tab}_tab` });
  };
  const excludedLanguageCodes = config.IS_WHITELABEL_APP ? [] : ["zh"];
  const availableLanguages = getAvailableLanguages(userLanguage || "en", excludedLanguageCodes);
  const languages = availableLanguages;

  return (
    <div className={styles.container}>
      <div className={styles.modal_header}>
        <h4>{t("Select Preferences")}</h4>
      </div>

      <div className={styles.body}>
        <div className={styles.sidebar}>
          <div
            className={formatClassNames(
              styles.sidebarItem,
              activeTab === "language" && styles.active,
            )}
            onClick={() => handleActiveTabChange("language")}
            role="none"
          >
            {t("Language")}
            <span className={styles.checkIcon}>{activeTab === "language" ? "✓" : ""}</span>
          </div>

          {isCurrencyFeatureEnabled && (
            <div
              className={formatClassNames(
                styles.sidebarItem,
                activeTab === "currency" && styles.active,
              )}
              onClick={() => handleActiveTabChange("currency")}
              role="none"
            >
              {t("Currency")}
              <span className={styles.checkIcon}>{activeTab === "currency" ? "✓" : ""}</span>
            </div>
          )}
        </div>

        <div className={styles.content}>
          <ul className={styles.list}>
            {activeTab === "language" &&
              languages.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    className={formatClassNames(
                      styles.option,
                      selectedLanguage === lang.code && styles.active,
                    )}
                    onClick={() => setSelectedLanguage(lang.code)}
                  >
                    <span className={styles.left}>
                      {lang.flag && React.createElement(lang.flag)}
                      <span className={styles.optionLabel}>{lang.label}</span>
                    </span>
                    <span
                      className={formatClassNames(
                        styles.radio,
                        selectedLanguage === lang.code && styles.checked,
                      )}
                    />
                  </button>
                </li>
              ))}

            {isCurrencyFeatureEnabled &&
              activeTab === "currency" &&
              currencies.map((cur, index) => (
                <React.Fragment key={cur.standard_name}>
                  {index === 0 && (
                    <li>
                      <button
                        type="button"
                        className={formatClassNames(
                          styles.option,
                          selectedCurrency === "default" && styles.optionActive,
                        )}
                        onClick={() => setSelectedCurrency("default")}
                      >
                        <span className={styles.left}>
                          <span className={styles.optionLabel}>Default</span>
                        </span>
                        <span
                          className={formatClassNames(
                            styles.radio,
                            selectedCurrency === "default" && styles.checked,
                          )}
                        />
                      </button>
                    </li>
                  )}

                  <li>
                    <button
                      type="button"
                      className={formatClassNames(
                        styles.option,
                        selectedCurrency === cur.standard_name && styles.active,
                      )}
                      onClick={() => setSelectedCurrency(cur.standard_name)}
                    >
                      <span className={styles.left}>
                        <span className={styles.optionLabel}>
                          {`${cur.standard_name} (${cur.symbol}) · ${
                            cur?.full_name?.split("(")[0]
                          }`}
                        </span>
                      </span>
                      <span
                        className={formatClassNames(
                          styles.radio,
                          selectedCurrency === cur.standard_name && styles.checked,
                        )}
                      />
                    </button>
                  </li>
                </React.Fragment>
              ))}
          </ul>
        </div>
      </div>

      <div className={styles.modal_footer}>
        <Button
          className={styles.btn_width}
          variant="outline"
          onClick={onReset}
          greyDisabled={
            selectedLanguage === defaultLanguage &&
            (!isCurrencyFeatureEnabled || selectedCurrency === "default")
          }
        >
          {t("Reset")}
        </Button>
        <Button className={styles.btn_apply} variant="primary" onClick={onApply}>
          {t("Apply")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(LanguageSelectorModalMobile, {
  componentName: "LanguageSelectorModalMobile",
  showForChina: true,
});
